import { IconArrowRight, IconBook, IconKey } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import type { ElementType } from "react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { isPublicApiSurfaceEnabled } from "@/lib/modules";
import { cn } from "@/lib/utils";

type DeveloperAccessTileProps = {
	to: string;
	icon: ElementType;
	iconClassName: string;
	iconWellClassName: string;
	title: string;
	description: string;
};

function DeveloperAccessTile({
	to,
	icon: Icon,
	iconClassName,
	iconWellClassName,
	title,
	description,
}: DeveloperAccessTileProps) {
	return (
		<Link
			to={to}
			className={cn(
				"group flex items-start gap-4 rounded-xl border border-border/60 bg-muted/15 p-4",
				"transition-all duration-200 hover:border-primary/35 hover:bg-muted/30 hover:shadow-sm",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
			)}
		>
			<div
				className={cn(
					"flex size-11 shrink-0 items-center justify-center rounded-lg",
					iconWellClassName,
				)}
			>
				<Icon className={cn("size-5", iconClassName)} aria-hidden />
			</div>
			<div className="min-w-0 flex-1 space-y-1">
				<p className="font-medium leading-none text-foreground group-hover:text-primary">
					{title}
				</p>
				<p className="text-pretty text-sm leading-relaxed text-muted-foreground">
					{description}
				</p>
			</div>
			<IconArrowRight
				className="mt-0.5 size-4 shrink-0 text-muted-foreground/50 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-primary"
				aria-hidden
			/>
		</Link>
	);
}

export function DeveloperAccessCard() {
	if (!isPublicApiSurfaceEnabled()) {
		return null;
	}

	return (
		<Card className="shadow-sm">
			<CardHeader className="pb-4">
				<div className="flex items-center gap-3">
					<div className="rounded-lg bg-emerald-500/10 p-2">
						<IconKey className="size-5 text-emerald-600" aria-hidden />
					</div>
					<div>
						<CardTitle className="text-xl">Developer access</CardTitle>
						<CardDescription className="text-pretty">
							Programmatic access to this team via scoped API keys.
						</CardDescription>
					</div>
				</div>
			</CardHeader>
			<CardContent className="space-y-4 pt-0">
				<div className="grid max-w-md gap-3">
					<DeveloperAccessTile
						to="/team/settings/api-keys"
						icon={IconKey}
						iconWellClassName="bg-emerald-500/10"
						iconClassName="text-emerald-600"
						title="API keys"
						description="Create live or test keys with granular scopes for the versioned REST API."
					/>
				</div>
				<Link
					to="/docs"
					className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
				>
					<IconBook className="size-4" aria-hidden />
					View developer documentation
					<IconArrowRight className="size-3.5" aria-hidden />
				</Link>
			</CardContent>
		</Card>
	);
}
